import React from "react";
import CONSOLE from "src/utils/console";
import Login from "src/components/login";
import { Navigate } from "@tanstack/react-router";
import WS from "src/api/ws";
import { SimpleAccount } from "src/api/generated/frontend_common_v1";
import { ApiCommon } from "src/api/api_common";

/** The global {@link AccountProvider} instance */
let ACCOUNT_PROVIDER: AccountProvider | null = null;

/** Data provided by the {@link ACCOUNT_CONTEXT} */
export type AccountContext = {
    /** The currently logged-in account */
    account: SimpleAccount;

    /** Reload the account's information */
    reset: () => void;
};

/** {@link React.Context} to access {@link SimpleAccount account information} */
const ACCOUNT_CONTEXT = React.createContext<AccountContext>({
    account: {
        display_name: "",
        uuid: "",
        mail: "",
        permissions: "Full",
    },

    /**
     * Reset the account's information
     */
    reset: () => {},
});
ACCOUNT_CONTEXT.displayName = "AccountContext";
export default ACCOUNT_CONTEXT;

/**
 * The properties of the account provider
 */
type AccountProviderProps = {
    /** The children of the properties */
    children: React.ReactNode | Array<React.ReactNode>;
};

/**
 * The state of the account provider
 */
type AccountProviderState = {
    /** The account */
    account: SimpleAccount | "unauthenticated" | "loading";
};

/**
 * Component for managing and providing the {@link AccountContext}
 *
 * This is a **singleton** only use at most **one** instance in your application.
 */
export class AccountProvider extends React.Component<AccountProviderProps, AccountProviderState> {
    state: AccountProviderState = { account: "loading" };

    fetching: boolean = false;

    /**
     * Fetch the account
     */
    fetchAccount = async () => {
        // Guard against a lot of calls
        if (this.fetching) return;
        this.fetching = true;

        this.setState({ account: "loading" });

        const account = await ApiCommon.accounts.getMe();
        if (account.permissions === "Full") {
            WS.connect(`${window.location.origin.replace("http", "ws")}/api/frontend/v1/internal/websocket`);
        } else {
            WS.connect(`${window.location.origin.replace("http", "ws")}/api/frontend/v1/customer/websocket`);
        }
        window.localStorage.setItem("mail", account.mail);
        // dayjs.locale(user.preferred_lang.toLocaleLowerCase());
        this.setState({ account });

        // Clear guard against a lot of calls
        this.fetching = false;
    };

    /**
     * Hook when the component mounts
     */
    componentDidMount() {
        this.fetchAccount().then();

        // Register as global singleton
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        if (ACCOUNT_PROVIDER === null) ACCOUNT_PROVIDER = this;
        else if (ACCOUNT_PROVIDER === this) CONSOLE.error("AccountProvider did mount twice");
        else CONSOLE.error("Two instances of AccountProvider are used");
    }

    /**
     * Hook when the component will unmount
     */
    componentWillUnmount() {
        // Deregister as global singleton
        if (ACCOUNT_PROVIDER === this) ACCOUNT_PROVIDER = null;
        else if (ACCOUNT_PROVIDER === null) CONSOLE.error("AccountProvider instance did unmount twice");
        else CONSOLE.error("Two instances of AccountProvider are used");
    }

    /**
     * The render function
     *
     * @returns The JSX component
     */
    render() {
        switch (this.state.account) {
            case "loading":
                return <div></div>;
            case "unauthenticated":
                return (
                    <>
                        <Navigate to="/" />
                        <Login
                            onLogin={() => {
                                this.fetchAccount();
                            }}
                        />
                    </>
                );
            default:
                return (
                    <ACCOUNT_CONTEXT.Provider
                        value={{
                            account: this.state.account,
                            reset: this.fetchAccount,
                        }}
                    >
                        {this.props.children}
                    </ACCOUNT_CONTEXT.Provider>
                );
        }
    }
}
